import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import gsap from "gsap";
import { currentHash, scrollToSection, watchSections } from "../app/sectionAnchor";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useSiteConfig, resolveImageUrl } from "../config/SiteConfigContext";
import { useIsMobile } from "../hooks/useIsMobile";
import { RichText } from "../components/RichText";
import { useCart } from "../cart/CartContext";
import { ga4 } from "../analytics/ga4";
import { productPath, pathFor } from "../app/paths";

gsap.registerPlugin(ScrollTrigger);

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8000";

interface ProductMedia {
  id: string;
  type: string;
  url: string;
  sort_order: number;
}
interface CompositionItem {
  name: string;
  description: string;
  image_url?: string;
}
interface CollectionProduct {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  price_cents: number;
  currency: string;
  weight_grams: number | null;
  dimensions: string | null;
  moq?: number | null;
  media: ProductMedia[];
  composition: { items: CompositionItem[] } | null;
}
interface CollectionData {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  image_url: string | null;
  products: CollectionProduct[];
}

export function Collections() {
  const { locale } = useParams();
  const { t } = useTranslation();
  const cfg = useSiteConfig();
  const isMobile = useIsMobile();
  const { addItem } = useCart();
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [collections, setCollections] = useState<CollectionData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [addedId, setAddedId] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError("");
    const lang = locale || "it";
    fetch(`${API_BASE}/api/v1/shop/collections?lang=${lang}`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(async (list: { slug: string }[]) => {
        const full = await Promise.all(
          list.filter((c) => c.slug).map((c) =>
            fetch(`${API_BASE}/api/v1/shop/collections/${c.slug}?lang=${lang}`).then((r) => r.json())
          )
        );
        setCollections(full);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [locale]);

  const fmt = (cents: number) => (cents / 100).toFixed(2).replace(".", ",");

  const handleAddToCart = (product: CollectionProduct) => {
    const mainImage = product.media.find((m) => m.type === "image");
    addItem({
      id: product.id,
      title: product.title,
      slug: product.slug,
      price_cents: product.price_cents,
      currency: product.currency,
      image: mainImage?.url,
    }, product.moq ?? undefined); // quantità iniziale = MOQ del prodotto (Marzamemi 36, Cinque Terre 24…)
    setAddedId(product.id);
    setTimeout(() => setAddedId(null), 2000);
  };

  useLayoutEffect(() => {
    if (loading || !wrapperRef.current) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(".gs-col-hero > *", { opacity: 0, y: 24 }, { opacity: 1, y: 0, duration: 0.8, ease: "expo.out", stagger: 0.12 });

      gsap.utils.toArray<HTMLElement>(".gs-col-section").forEach((el) => {
        gsap.fromTo(el, { opacity: 0, y: 60 }, {
          opacity: 1, y: 0, duration: 1, ease: "expo.out",
          scrollTrigger: { trigger: el, start: "top 85%", once: true },
        });
      });

      gsap.utils.toArray<HTMLElement>(".gs-col-grid").forEach((el) => {
        gsap.fromTo(el.children, { opacity: 0, y: 40 }, {
          opacity: 1, y: 0, duration: 0.8, stagger: 0.1, ease: "expo.out",
          scrollTrigger: { trigger: el, start: "top 85%", once: true },
        });
      });
    }, wrapperRef);
    return () => ctx.revert();
  }, [loading, collections.length]);

  /* ─── Ancoraggio alle sezioni ────────────────────────────────────────────
     Il salto va fatto QUI e non lasciato al browser: quello nativo avviene
     alla navigazione, quando le collezioni sono ancora in rete e le sezioni
     non esistono. È il motivo per cui `#citta` sembrava non funzionare. */
  useEffect(() => {
    if (loading || collections.length === 0) return;

    const ids = collections.map((c) => c.slug).filter(Boolean);
    const watcher = watchSections(ids);

    const target = currentHash();
    if (target) {
      // Un fotogramma di attesa: le sezioni sono nel DOM ma il browser non ha
      // ancora calcolato le posizioni, e senza questo si salta a una
      // coordinata che cambia subito dopo.
      requestAnimationFrame(() => {
        watcher.suspend();
        scrollToSection(target, (el) => {
          // La sezione nasce trasparente per l'animazione d'ingresso: senza
          // questo si arriva dove si voleva e non si vede nulla.
          gsap.set(el, { opacity: 1, y: 0 });
          // Le posizioni di partenza delle altre animazioni sono state
          // calcolate su una pagina ferma in cima.
          ScrollTrigger.refresh();
        });
      });
    }

    return () => watcher.stop();
  }, [loading, collections]);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <div style={{ width: 24, height: 24, border: "1.5px solid rgba(28,75,59,0.89)", borderTopColor: cfg.color_dark, borderRadius: "50%", animation: "spin .7s linear infinite" }} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: "center", padding: 80 }}>
        <p style={{ color: "#991b1b", fontSize: 13, marginBottom: 16 }}>{error}</p>
        <button onClick={() => window.location.reload()} style={{ background: cfg.color_dark, color: "#fff", border: "none", padding: "10px 24px", fontSize: 12, fontWeight: 500, cursor: "pointer", letterSpacing: 1, textTransform: "uppercase" }}>
          {t("common.retry")}
        </button>
      </div>
    );
  }

  const totalConfezioni = collections.reduce((sum, c) => sum + c.products.length, 0);

  return (
    <div ref={wrapperRef}>
      {/* ═══ HERO ═══ */}
      <section style={{ background: cfg.color_dark, padding: isMobile ? "80px 24px 60px" : "120px 48px 80px" }}>
        <div className="gs-col-hero" style={{ maxWidth: 1280, margin: "0 auto" }}>
          <p style={{ fontSize: 10, fontWeight: 600, letterSpacing: 3, textTransform: "uppercase", color: "#fff", margin: "0 0 20px" }}>
            {t("collectionsIndex.overline")}
          </p>
          <h1 style={{
            fontFamily: cfg.font_heading, fontSize: isMobile ? 32 : 52, fontWeight: 400,
            color: "#fff", margin: "0 0 16px", lineHeight: 1.05, letterSpacing: -1,
          }}>
            {t("collectionsIndex.heroTitle")}
          </h1>
          <div style={{ width: 40, height: 1, background: "rgba(255,255,255,0.15)", marginBottom: 16 }} />
          <p style={{ fontSize: isMobile ? 13 : 15, color: "#fff", lineHeight: 1.7, margin: 0, maxWidth: 440 }}>
            {t("collectionsIndex.heroSubtitle")}
          </p>
          <div style={{ display: "flex", gap: 16, marginTop: 24, alignItems: "center" }}>
            <span style={{ fontSize: 11, fontWeight: 500, letterSpacing: 1.5, textTransform: "uppercase", color: "#fff" }}>
              {collections.length} {t("nav.collezioni")} · {totalConfezioni} {t("collectionsTree.confezioni")}
            </span>
          </div>
        </div>
      </section>

      {/* ═══ COLLECTION SECTIONS ═══ */}
      {collections.map((col, colIdx) => {
        const isEven = colIdx % 2 === 0;

        return (
          <section
            key={col.id}
            // Lo slug e non l'id interno: è già l'identificatore stabile del
            // progetto, compare negli indirizzi delle collezioni ed è leggibile
            // — `#citta` dice cosa si sta guardando, un UUID no.
            id={col.slug}
            // La sezione può essere il bersaglio di un salto: senza questo, il
            // browser ci scorre sotto l'intestazione appiccicata e il titolo
            // resta nascosto.
            className="gs-col-section"
            style={{
              background: isEven ? cfg.color_body_bg : cfg.color_light_bg,
              padding: isMobile ? "56px 24px" : "88px 48px",
              // Bersaglio di un salto: senza, il browser ci scorre sotto
              // l'intestazione appiccicata e il titolo resta nascosto.
              scrollMarginTop: 88,
            }}
          >
            <div style={{ maxWidth: 1280, margin: "0 auto" }}>
              {/* ── Collection header ── */}
              <div style={{
                display: isMobile ? "block" : "flex",
                alignItems: "flex-end",
                justifyContent: "space-between",
                gap: 32,
                marginBottom: isMobile ? 32 : 48,
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                    <span style={{
                      fontSize: 10, fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase",
                      color: cfg.color_accent,
                    }}>
                      {String(colIdx + 1).padStart(2, "0")}
                    </span>
                    <div style={{ width: 32, height: 1, background: cfg.color_muted }} />
                    <span style={{ fontSize: 10, fontWeight: 500, letterSpacing: 2, textTransform: "uppercase", color: cfg.color_muted }}>
                      {col.products.length} {t("collectionsTree.confezioni")}
                    </span>
                  </div>
                  <h2 style={{
                    fontFamily: cfg.font_heading, fontSize: isMobile ? 28 : 40, fontWeight: 400,
                    color: cfg.color_dark, margin: "0 0 10px", lineHeight: 1.1, letterSpacing: -0.5,
                  }}>
                    {col.title}
                  </h2>
                  {col.description && (
                    <RichText html={col.description} style={{ fontSize: 14, color: "rgba(28,75,59,0.86)", lineHeight: 1.7, maxWidth: 500 }} />
                  )}
                </div>

                {/* Collection cover image — DISATTIVATA per ora: immagine piccola (240x168) a lato dell'header,
                    visibile solo su desktop (non su smartphone per via di !isMobile). Rimuovere il commento per riattivarla. */}
                {/*
                {col.image_url && !isMobile && (
                  <div style={{
                    width: 240, height: 168, flexShrink: 0, overflow: "hidden",
                    position: "relative",
                  }}>
                    <img
                      src={resolveImageUrl(col.image_url)}
                      alt={col.title}
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                    <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, rgba(0,0,0,0.05), rgba(0,0,0,0.2))" }} />
                  </div>
                )}
                */}
              </div>

              {/* ── Confezioni grid ── */}
              <div
                className="gs-col-grid"
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : col.products.length === 1 ? "1fr" : col.products.length === 2 ? "1fr 1fr" : "repeat(3, 1fr)",
                  gap: isMobile ? 20 : 24,
                }}
              >
                {col.products.map((product) => {
                  const thumb = product.media.find((m) => m.type === "image");
                  const isAdded = addedId === product.id;

                  return (
                    <div
                      key={product.id}
                      style={{
                        background: "#fff",
                        border: "1px solid rgba(28,75,59,0.89)",
                        overflow: "hidden",
                        transition: "box-shadow 0.3s",
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 8px 28px rgba(0,0,0,0.07)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; }}
                    >
                      {/* Image */}
                      <Link to={productPath(locale, product.slug)} style={{ display: "block", textDecoration: "none" }}>
                        <div style={{
                          aspectRatio: isMobile ? "16/10" : "4/3",
                          background: cfg.color_light_bg,
                          overflow: "hidden",
                          position: "relative",
                        }}>
                          {thumb ? (
                            <img
                              src={resolveImageUrl(thumb.url)}
                              alt={product.title}
                              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", transition: "transform 0.5s" }}
                              onMouseEnter={(e) => { (e.target as HTMLImageElement).style.transform = "scale(1.04)"; }}
                              onMouseLeave={(e) => { (e.target as HTMLImageElement).style.transform = "scale(1)"; }}
                            />
                          ) : (
                            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#ddd" strokeWidth="1">
                                <rect x="3" y="8" width="18" height="13" rx="2" /><path d="M12 8V21" /><path d="M3 13h18" /><path d="M8 8c0-2 1-4 4-4s4 2 4 4" />
                              </svg>
                            </div>
                          )}
                        </div>
                      </Link>

                      {/* Info */}
                      <div style={{ padding: isMobile ? "16px" : "20px 24px" }}>
                        <Link to={productPath(locale, product.slug)} style={{ textDecoration: "none", color: "inherit" }}>
                          <h3 style={{
                            fontFamily: cfg.font_heading, fontSize: isMobile ? 18 : 20, fontWeight: 400,
                            color: cfg.color_dark, margin: "0 0 6px", lineHeight: 1.2, letterSpacing: -0.3,
                          }}>
                            {product.title}
                          </h3>
                        </Link>

                        <p style={{ fontSize: 18, fontWeight: 400, color: cfg.color_dark, margin: "0 0 2px", fontVariantNumeric: "tabular-nums" }}>
                          {fmt(product.price_cents)} €
                        </p>
                        {cfg.vat_rate_percent > 0 && (
                          <p style={{ fontSize: 10.5, color: cfg.color_muted, margin: "0 0 12px" }}>
                            {t("collectionDetail.vatExcluded", { rate: cfg.vat_rate_percent })}
                          </p>
                        )}

                        {product.description && (
                          <RichText html={product.description} style={{
                            fontSize: 13, color: "rgba(28,75,59,0.86)", lineHeight: 1.6, marginBottom: 16,
                            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as const, overflow: "hidden",
                          }} />
                        )}

                        {/* Composition preview */}
                        {product.composition && product.composition.items.length > 0 && (
                          <div style={{ marginBottom: 16 }}>
                            <p style={{ fontSize: 9, fontWeight: 600, letterSpacing: 2, textTransform: "uppercase", color: cfg.color_muted, margin: "0 0 8px" }}>
                              {t("collectionDetail.contains")} — {product.composition.items.length}
                            </p>
                            <div style={{ display: "flex", gap: 4 }}>
                              {product.composition.items.slice(0, 5).map((item, i) => (
                                <div key={i} style={{ width: 46, height: 46, overflow: "hidden", background: "#f5f5f5", flexShrink: 0 }}>
                                  {item.image_url ? (
                                    <img src={resolveImageUrl(item.image_url)} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                                  ) : (
                                    <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                      <div style={{ width: 3, height: 3, background: "#ddd" }} />
                                    </div>
                                  )}
                                </div>
                              ))}
                              {product.composition.items.length > 5 && (
                                <div style={{ width: 46, height: 46, background: "#f5f5f5", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: "rgba(28,75,59,0.86)", fontWeight: 600 }}>
                                  +{product.composition.items.length - 5}
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Actions */}
                        <div style={{ display: "flex", gap: 8 }}>
                          <button
                            onClick={() => handleAddToCart(product)}
                            style={{
                              flex: 1,
                              background: isAdded ? cfg.color_accent : cfg.color_dark,
                              color: "#fff", border: "none", padding: "12px 16px",
                              fontSize: 10, fontWeight: 600, letterSpacing: 1.5, textTransform: "uppercase",
                              cursor: "pointer", transition: "background 0.25s",
                              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                            }}
                            onMouseEnter={(e) => { if (!isAdded) e.currentTarget.style.background = "#333"; }}
                            onMouseLeave={(e) => { if (!isAdded) e.currentTarget.style.background = isAdded ? cfg.color_accent : cfg.color_dark; }}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" /><line x1="3" y1="6" x2="21" y2="6" /><path d="M16 10a4 4 0 01-8 0" />
                            </svg>
                            {isAdded ? t("cart.added") : t("products.addToCart")}
                          </button>
                          <Link
                            to={productPath(locale, product.slug)}
                            style={{
                              border: "1px solid rgba(28,75,59,0.89)", color: cfg.color_dark,
                              padding: "12px 16px", textDecoration: "none",
                              fontSize: 10, fontWeight: 600, letterSpacing: 1.5, textTransform: "uppercase",
                              transition: "background 0.2s",
                              display: "flex", alignItems: "center", gap: 6,
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = "#f5f5f5"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                          >
                            {t("collectionDetail.viewDetails")}
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
                          </Link>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        );
      })}

      {/* ═══ COMBINA I PACCHI — richiesta offerta personalizzata ═══ */}
      <section className="gs-col-section" style={{ background: cfg.color_light_bg, padding: isMobile ? "48px 24px" : "72px 48px" }}>
        <div style={{
          maxWidth: 820, margin: "0 auto",
          background: cfg.color_body_bg,
          border: `1px solid ${cfg.color_accent}`,
          padding: isMobile ? "32px 24px" : "48px 56px",
          textAlign: "center",
        }}>
          <div style={{ width: 40, height: 1, background: cfg.color_accent, margin: "0 auto 20px" }} />
          <h2 style={{
            fontFamily: cfg.font_heading, fontSize: isMobile ? 22 : 30, fontWeight: 400,
            color: cfg.color_dark, margin: "0 0 14px", lineHeight: 1.15, letterSpacing: -0.3,
          }}>
            {t("collectionsIndex.combineTitle")}
          </h2>
          <p style={{ fontSize: isMobile ? 14 : 15, color: "rgba(28,75,59,0.86)", lineHeight: 1.7, margin: "0 auto 28px", maxWidth: 520 }}>
            {t("collectionsIndex.combineText")}
          </p>
          <div style={{ display: "flex", justifyContent: "center", gap: 12, flexWrap: "wrap" }}>
            <Link
              to={pathFor("contact", locale)}
              style={{
                display: "inline-block", padding: "12px 28px", fontSize: 11, fontWeight: 600,
                letterSpacing: 1.5, textTransform: "uppercase", color: "#fff", textDecoration: "none",
                background: cfg.color_dark, transition: "opacity 0.2s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.85"; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
            >
              {t("collectionsIndex.combineCta")}
            </Link>
            <a
              href={`mailto:${cfg.contact_email || "info@granborgo.com"}`}
              onClick={() => ga4.contactClick("email", "collections")}
              style={{
                display: "inline-block", padding: "12px 28px", fontSize: 11, fontWeight: 600,
                letterSpacing: 1, textTransform: "none", color: cfg.color_dark, textDecoration: "none",
                border: `1px solid rgba(28,75,59,0.89)`, transition: "background 0.2s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(28,75,59,0.04)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              {cfg.contact_email || "info@granborgo.com"}
            </a>
            <a
              href={`tel:${(cfg.contact_phone || "+4967629499360").replace(/\s/g, "")}`}
              onClick={() => ga4.contactClick("phone", "collections")}
              style={{
                display: "inline-block", padding: "12px 28px", fontSize: 11, fontWeight: 600,
                letterSpacing: 1, textTransform: "none", color: cfg.color_dark, textDecoration: "none",
                border: `1px solid rgba(28,75,59,0.89)`, transition: "background 0.2s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(28,75,59,0.04)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              {cfg.contact_phone || "+4967629499360"}
            </a>
          </div>
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section style={{ background: cfg.color_body_bg, padding: isMobile ? "56px 24px" : "80px 48px", textAlign: "center" }}>
        <div style={{ maxWidth: 480, margin: "0 auto" }}>
          <p style={{ fontSize: 10, fontWeight: 600, letterSpacing: 3, textTransform: "uppercase", color: cfg.color_muted, margin: "0 0 12px" }}>
            {t("collectionsIndex.giftBox")}
          </p>
          <h2 style={{ fontFamily: cfg.font_heading, fontSize: isMobile ? 22 : 28, fontWeight: 400, color: cfg.color_dark, margin: "0 0 20px", letterSpacing: -0.3 }}>
            {t("fiscalita.ctaTitle")}
          </h2>
          <p style={{ fontSize: 14, color: "rgba(28,75,59,0.86)", lineHeight: 1.7, margin: "0 0 28px" }}>
            {t("fiscalita.ctaText")}
          </p>
          <div style={{ display: "flex", justifyContent: "center", gap: 12, flexWrap: "wrap" }}>
            <Link
              to={pathFor("about", locale)}
              style={{
                display: "inline-block", padding: "12px 28px", fontSize: 11, fontWeight: 600,
                letterSpacing: 1.5, textTransform: "uppercase", color: cfg.color_dark, textDecoration: "none",
                border: `1px solid rgba(28,75,59,0.89)`, transition: "background 0.2s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(28,75,59,0.04)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              {t("nav.about")}
            </Link>
            <Link
              to={pathFor("fiscal", locale)}
              style={{
                display: "inline-block", padding: "12px 28px", fontSize: 11, fontWeight: 600,
                letterSpacing: 1.5, textTransform: "uppercase", color: "#fff", textDecoration: "none",
                background: cfg.color_dark, transition: "opacity 0.2s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.85"; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
            >
              {t("nav.fiscalita")}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
